/**
 * 腾讯域：策略执行记录 API 服务（CLAUDE.md §前端 API 服务层 — 按域分目录）。
 *
 * 与后端 Pydantic 模型 src.tencent.dtos.auto_ad_operate_result 对齐。
 *   - GET  /api/v1/tencent/auto-ad-operate-result            → 分页过滤查询（含媒体交互状态/响应报文）
 *   - GET  /api/v1/tencent/auto-ad-operate-result/enums      → 操作类型/代理下拉枚举
 *   - GET  /api/v1/tencent/auto-ad-operate-result/dashboard  → DashBoard 操作原因聚合
 *
 * 数据源 t_tencent_auto_ad_operate_result（do_check 实际执行 update_ad 的结果日志）。
 */

import { request } from "../apiClient";

/** 策略执行记录单行（与后端 AutoAdOperateResultRecord 对齐）。 */
export interface AutoAdOperateResultRecord {
  account_id: string;
  adgroup_id: string;
  action_name: string;
  model_condition: string;
  daili: string;
  status: string;
  message: string;
  created_time: string;
  ds: string;
}

/**
 * 策略执行记录查询入参（与后端 AutoAdOperateResultQuery 对齐，字段均可选）。
 * 用 `type` 而非 `interface`：TS 中只有 type 别名可隐式满足 apiClient 的
 * `Record<string, ...>` 索引签名（interface 不行）。
 */
export type AutoAdOperateResultQuery = {
  ds_start?: string;
  ds_end?: string;
  account_id?: string;
  adgroup_id?: string;
  action_name?: string;
  daili?: string;
  condition?: string;
  page?: number;
  page_size?: number;
};

/** 策略执行记录分页结果（与后端 AutoAdOperateResultPage 对齐）。 */
export interface AutoAdOperateResultPage {
  items: AutoAdOperateResultRecord[];
  total: number;
  page: number;
  page_size: number;
}

/** 策略执行记录下拉枚举（与后端 AutoAdOperateResultEnums 对齐）。 */
export interface AutoAdOperateResultEnums {
  action_name: string[];
  daili: string[];
}

/** 分页过滤查询策略执行记录。 */
export async function getAutoAdOperateResult(
  query: AutoAdOperateResultQuery,
): Promise<AutoAdOperateResultPage> {
  const data = await request<AutoAdOperateResultPage | null>(
    "/api/v1/tencent/auto-ad-operate-result",
    { query },
  );
  return (
    data ?? {
      items: [],
      total: 0,
      page: query.page ?? 1,
      page_size: query.page_size ?? 20,
    }
  );
}

/** 取操作类型 / 代理下拉枚举（DISTINCT 自表）。 */
export async function getAutoAdOperateResultEnums(): Promise<AutoAdOperateResultEnums> {
  const data = await request<AutoAdOperateResultEnums | null>(
    "/api/v1/tencent/auto-ad-operate-result/enums",
  );
  return data ?? { action_name: [], daili: [] };
}

/**
 * DashBoard（操作原因聚合）查询入参（与后端 AutoAdOperateDashboardQuery 对齐，均可选；
 * status 固定 ('success', '—') 由后端模板写死，非入参）。
 * 用 `type` 的原因同 AutoAdOperateResultQuery（满足 Record 索引签名）。
 */
export type AutoAdOperateDashboardQuery = {
  ds_start?: string;
  ds_end?: string;
  condition?: string;
};

/** DashBoard 单行聚合结果（与后端 AutoAdOperateDashboardRecord 对齐）。 */
export interface AutoAdOperateDashboardRecord {
  model_condition: string;
  data_cnt: number;
  yike_cnt: number;
  yunrui_cnt: number;
  pairui_cnt: number;
  liou_cnt: number;
}

/** DashBoard 聚合结果（与后端 AutoAdOperateDashboardResult 对齐，非分页）。 */
export interface AutoAdOperateDashboardResult {
  items: AutoAdOperateDashboardRecord[];
}

/** 按操作原因聚合查询（GROUP BY model_condition + 四代理命中量）。 */
export async function getAutoAdOperateDashboard(
  query: AutoAdOperateDashboardQuery,
): Promise<AutoAdOperateDashboardResult> {
  const data = await request<AutoAdOperateDashboardResult | null>(
    "/api/v1/tencent/auto-ad-operate-result/dashboard",
    { query },
  );
  return data ?? { items: [] };
}
